import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";

/**
 * Termini di Servizio pubblici.
 * Contenuto gestito da admin in `/admin/public-content` → tab "Termini di Servizio".
 * Se non c'è ancora un documento pubblicato, viene mostrato un placeholder.
 */
export default function TermsOfService() {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/policy?slug=terms")
      .then(r => setPolicy(r.data && !r.data.empty ? r.data : null))
      .catch(() => setPolicy(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-purple-700 hover:underline mb-6" data-testid="terms-back">
          <ArrowLeft size={16} /> Torna alla home
        </Link>
        <div className="label-eyebrow">Legale</div>
        <h1 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-2 mb-2">
          {policy?.title || "Termini di Servizio"}
        </h1>
        {policy?.version && (
          <p className="text-sm text-muted-foreground">
            Versione {policy.version}
            {policy.updated_at && ` — ultimo aggiornamento ${new Date(policy.updated_at).toLocaleDateString("it-IT")}`}
          </p>
        )}

        {loading ? (
          <div className="mt-8 space-y-3">
            <div className="h-4 bg-slate-100 rounded w-3/4 animate-pulse" />
            <div className="h-4 bg-slate-100 rounded w-2/3 animate-pulse" />
            <div className="h-4 bg-slate-100 rounded w-4/5 animate-pulse" />
          </div>
        ) : policy?.body ? (
          <article className="prose prose-slate prose-sm md:prose-base max-w-none mt-6" data-testid="terms-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight mt-8 mb-3">{children}</h2>,
                h2: ({ children }) => <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight mt-8 mb-3">{children}</h2>,
                h3: ({ children }) => <h3 className="font-semibold text-base md:text-lg mt-5 mb-2">{children}</h3>,
                p: ({ children }) => <p className="text-sm md:text-base text-slate-700 leading-relaxed mb-3">{children}</p>,
                ul: ({ children }) => <ul className="list-disc list-inside text-sm md:text-base text-slate-700 space-y-1 mb-3">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal list-inside text-sm md:text-base text-slate-700 space-y-1 mb-3">{children}</ol>,
                a: ({ href = "", children }) => href.startsWith("/") ? (
                  <Link to={href} className="text-purple-700 underline">{children}</Link>
                ) : (
                  <a href={href} target="_blank" rel="noreferrer" className="text-purple-700 underline">{children}</a>
                ),
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                code: ({ children }) => <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{children}</code>,
              }}
            >
              {policy.body}
            </ReactMarkdown>
          </article>
        ) : (
          <div className="mt-8 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="terms-empty">
            I Termini di Servizio non sono ancora stati pubblicati. L'amministratore può caricarli da{" "}
            <span className="font-mono text-xs bg-white px-1 py-0.5 rounded border">Impostazioni Contenuti pubblici → Termini di Servizio</span>.
          </div>
        )}

        <div className="mt-10 pt-6 border-t text-sm text-muted-foreground">
          Vedi anche la nostra{" "}
          <Link to="/privacy" className="text-purple-700 underline" data-testid="terms-to-privacy">
            Privacy Policy
          </Link>.
        </div>
      </div>
    </div>
  );
}
